// src/utils/normalizeEvent.js
/**
 * Universal event normalizer - ensures consistent field names across all event sources
 *
 * Handles raw events from:
 * - Typesense search results (varying field names)
 * - Postgres hydrated events (canonical field names)
 * - Legacy API responses (mixed field names)
 *
 * Returns events with canonical field names expected by UI components:
 * event_id, event_name, event_location, event_image_link, event_date, etc.
 */

/**
 * Helper to convert UNIX timestamp (seconds or milliseconds) to ISO string
 * Handles both epoch seconds and epoch milliseconds
 */
const epochToIso = (n) => {
  if (n == null) return null;
  const num = Number(n);
  if (!Number.isFinite(num)) return null;
  // Auto-detect: numbers > 1e12 are likely milliseconds, otherwise seconds
  const ms = num > 1e12 ? num : num * 1000;
  return new Date(ms).toISOString();
};

/**
 * Convert image URLs to absolute HTTPS format
 * - Resolves protocol-relative URLs (//example.com/image.jpg)
 * - Resolves relative URLs (/images/photo.jpg)
 * - Upgrades HTTP to HTTPS on production pages to avoid mixed-content blocking
 */
const toAbsoluteHttps = (url) => {
  if (!url || typeof url !== 'string') return null;
  let u = url.trim();
  if (!u) return null;

  // Resolve protocol-relative URLs
  if (u.startsWith('//')) {
    u = (typeof window !== 'undefined' ? window.location.protocol : 'https:') + u;
  }

  // Resolve relative URLs
  if (u.startsWith('/')) {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://localhost';
    u = origin + u;
  }

  try {
    const parsed = new URL(u);
    // Upgrade HTTP to HTTPS on production pages
    if (typeof window !== 'undefined' && window.location.protocol === 'https:' && parsed.protocol === 'http:') {
      parsed.protocol = 'https:';
      return parsed.toString();
    }
    return parsed.toString();
  } catch {
    return null;
  }
};

export default function normalizeEvent(raw = {}) {
  const e = raw || {};

  // IDs - try all possible ID field names
  const event_id = e.event_id || e.id || e.eventId || e.ts_id || e.uuid || e.doc_id;

  // Names / title - prioritize event_name, fallback to place_name for reviews
  const event_name = e.event_name || e.place_name || e.name || e.title || e.eventTitle || 'Untitled Event';

  // Dates - handle various date field formats, including Typesense timestamps
  const event_date =
    e.event_date ||
    epochToIso(e.event_date_ts) ||
    e.start_date ||
    e.startDate ||
    e.start_time ||
    e.starts_at ||
    e.date_start ||
    epochToIso(e.created_at_ts);
  const event_end_date =
    e.event_end_date ||
    epochToIso(e.event_end_date_ts) ||
    e.end_date ||
    e.endDate ||
    e.end_time ||
    e.ends_at ||
    e.date_end;

  // Location - try multiple location field names
  const event_location = e.event_location || e.location_name || e.venue || e.address || e.place;

  // Image - comprehensive fallback chain for images with strict URL resolution
  const rawImage =
    e.event_image_link ??
    e.image_url ??
    e.event_image ??
    e.image ??
    e.coverImage ??
    e.cover ??
    e.poster_url ??
    e.poster ??
    e.mainImage ??
    e.thumbnail ??
    e.thumb ??
    e.image_link ??
    e.main_image_url ??
    e?.photo?.images?.large?.url ??
    null;

  // DEBUG: Log image normalization for ALL events to trace issues
  console.log(`🖼️ [normalizeEvent] Event ${event_id}:`, {
    name: event_name?.substring(0, 30),
    rawImage: rawImage ? (rawImage.substring(0, 80) + (rawImage.length > 80 ? '...' : '')) : 'NULL',
    hasEventImageLink: !!e.event_image_link,
    hasImageUrl: !!e.image_url,
    hasPosterUrl: !!e.poster_url
  });

  const event_image_link = toAbsoluteHttps(rawImage) ||
    'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=400&h=300&fit=crop'; // Placeholder: event/festival image

  // DEBUG: Log final result
  console.log(`🎨 [normalizeEvent] Event ${event_id} final image:`,
    event_image_link.substring(0, 80) + (event_image_link.length > 80 ? '...' : ''));

  // Website
  const website_link = e.website_link || e.website || e.url || e.link || null;

  // Lat/Lng - handle nested geo objects, direct properties, and Typesense location field
  let latitude = e.latitude ?? e.lat ?? (e.geo && e.geo.lat) ?? null;
  let longitude = e.longitude ?? e.lng ?? (e.geo && e.geo.lng) ?? null;

  // Typesense 'location' can be "lat, lon" string, [lat, lon] array, or {lat, lng} object
  if ((latitude == null || longitude == null) && e.location) {
    if (typeof e.location === 'string' && e.location.includes(',')) {
      const [la, lo] = e.location.split(',').map(s => parseFloat(s.trim()));
      if (Number.isFinite(la) && Number.isFinite(lo)) {
        latitude = la;
        longitude = lo;
      }
    } else if (Array.isArray(e.location) && e.location.length >= 2) {
      const [la, lo] = e.location;
      if (Number.isFinite(la) && Number.isFinite(lo)) {
        latitude = la;
        longitude = lo;
      }
    } else if (e.location && typeof e.location === 'object' && e.location.lat && e.location.lng) {
      latitude = e.location.lat;
      longitude = e.location.lng;
    }
  }

  // Ensure lat/lng are always numbers (Postgres NUMERIC columns return as strings in node-postgres)
  if (latitude != null) latitude = parseFloat(latitude);
  if (longitude != null) longitude = parseFloat(longitude);
  if (!Number.isFinite(latitude)) latitude = null;
  if (!Number.isFinite(longitude)) longitude = null;

  // Summary/Description
  const event_summary = e.event_summary || e.summary || e.description || '';

  // Category
  const main_category = e.main_category || e.category || e.type || null;

  // Build media_items fallback for reviews without hydrated media_items
  // (Typesense-only data has main_video_url and main_image_url but no review_images array)
  // Order respects reviewer's main_content_type preference
  let media_items = e.media_items || null;
  if (!media_items && e._source === 'review') {
    const videoUrl = e.main_video_url || e.video_link;
    const hasImage = event_image_link && !event_image_link.includes('unsplash.com');
    const videoItem = videoUrl ? { type: 'video', url: videoUrl, display_order: 0 } : null;
    const imageItem = hasImage ? { type: 'image', url: event_image_link, display_order: 0 } : null;

    const contentType = (e.main_content_type || 'image').toLowerCase();
    const isVideoMain = contentType === 'video' || contentType === 'video_link';

    media_items = [];
    if (isVideoMain) {
      if (videoItem) media_items.push(videoItem);
      if (imageItem) media_items.push(imageItem);
    } else {
      if (imageItem) media_items.push(imageItem);
      if (videoItem) media_items.push(videoItem);
    }
    if (media_items.length === 0) media_items = null;
  }

  // Return new object with all original fields PLUS guaranteed canonical fields
  return {
    ...e,
    event_id,
    event_name,
    event_date,
    event_end_date,
    event_location,
    event_image_link,
    additional_image_links: e.additional_image_links || [],
    website_link,
    latitude,
    longitude,
    event_summary,
    main_category,
    ...(media_items ? { media_items } : {})
  };
}

/**
 * Normalize an array of events
 */
export function normalizeEvents(events = []) {
  if (!Array.isArray(events)) return [];
  return events.map(normalizeEvent);
}
